import { Header, Helper, Loader, StandaloneIcon, Sup } from 'Components';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { arrayMove } from 'helpers/array-helpers';
import { handleColumnSave } from 'helpers/column-save';
import { useTranslation } from 'react-i18next';
import './TemplateTable.css';

const TemplateTable = ({
  data,
  template,
  keys,
  keyPairs,
  hasSettings,
  sort,
  isLoading,
  isDeleting,
  onSettingClick,
  footerTemplate,
  code,
  leadingColumnCount,
  trailingColumnCount,
  isTutorialShowing,
  isDraggable,
  className,
  handleSortClick,
  isRowLoading,
  ...rest
}) => {
  const TemplateItem = template;
  const FooterTemplate = footerTemplate;
  const { t } = useTranslation();

  const [dragIndexFrom, setDragIndexFrom] = useState(undefined);
  const [dragIndexTo, setDragIndexTo] = useState(undefined);

  const onDragStart = (i) => setDragIndexFrom(i);

  const onDragOver = (i) => {
    if (i !== dragIndexTo) {
      setDragIndexTo(i);
    }
  };

  const onDragEnd = () => {
    setDragIndexFrom(undefined);
    setDragIndexTo(undefined);
    handleColumnSave(arrayMove(keys, dragIndexFrom, dragIndexTo), rest.viewCode, rest.setAppState);
  };

  const formatHeaders = (key, i) => {
    let headerValue = <Sup>{keyPairs[key]}</Sup>;
    if (typeof keyPairs[key] !== 'string') {
      headerValue = keyPairs[key];
    }

    return (
      <Header
        padLeft={i === 0}
        key={key}
        keyVal={key}
        sort={sort}
        code={code}
        onDragStart={() => onDragStart(i)}
        onDragOver={() => onDragOver(i)}
        onDragEnd={onDragEnd}
        isHoverActive={dragIndexTo === i}
        isDraggable={isDraggable}
        className={`${className ? `${className}__th` : ''}`}
        handleSortClick={handleSortClick}
        hasManualSort={handleSortClick}
        sortable
      >
        {headerValue}
      </Header>
    );
  };

  return (
    <div className={`template-table ${isLoading || isDeleting ? 'template-table--hidden' : ''} ${className}`}>
      {(isLoading || isDeleting) && <Loader isSolidWhite />}
      {(!isLoading || !isDeleting) && (
        <table>
          <thead className={className ? `${className}__thead` : ''}>
            <tr>
              {Array.from({ length: leadingColumnCount }).map((_, i) => (
                <th key={`leading-${i}`} className={className ? `${className}__th` : ''} />
              ))}
              {keys.map(formatHeaders)}
              {Array.from({ length: trailingColumnCount }).map((_, i) => (
                <th key={`trailing-${i}`} className={className ? `${className}__th` : ''} />
              ))}
              {hasSettings && (
                <Fragment>
                  <th className={`${className ? `${className}__th` : ''} template-table__th--menu`} />
                  <th className={`${className ? className + '__th' : ''} template-table__th--menu-icon`}>
                    <Helper text={t('tutorials:common.1')}>
                      <StandaloneIcon
                        onClick={(e) => {
                          e.preventDefault();
                          onSettingClick(e);
                        }}
                        icon="settings"
                      />
                    </Helper>
                  </th>
                </Fragment>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((dataItem, i) => (
              <TemplateItem
                {...rest}
                keys={keys}
                keyPairs={keyPairs}
                {...rest}
                {...dataItem}
                tableIndex={i}
                data={data}
                key={i}
                isRowLoading={isRowLoading}
                isTutorialShowing={isTutorialShowing}
              />
            ))}
          </tbody>
          {footerTemplate && (
            <tfoot>
              <FooterTemplate {...rest} data={data} />
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
};

TemplateTable.propTypes = {
  isLoading: PropTypes.bool,
  keys: PropTypes.arrayOf(PropTypes.node),
  keyPairs: PropTypes.shape({}),
  data: PropTypes.arrayOf(PropTypes.shape({})),
  footerTemplate: PropTypes.func,
  template: PropTypes.func,
  isDeleting: PropTypes.bool,
  hasSettings: PropTypes.bool,
  onSettingClick: PropTypes.func,
  code: PropTypes.string,
  leadingColumnCount: PropTypes.number,
  trailingColumnCount: PropTypes.number,
  isTutorialShowing: PropTypes.bool,
  sort: PropTypes.string,
  isDraggable: PropTypes.bool,
  className: PropTypes.string,
  handleSortClick: PropTypes.func,
};

TemplateTable.defaultProps = {
  keys: [],
  keyPairs: {},
  data: [],
  leadingColumnCount: 0,
  trailingColumnCount: 0,
  className: '',
};

export default TemplateTable;



// WEBPACK FOOTER //
// ./src/components/TemplateTable/TemplateTable.jsx